"use client"

import { colors } from "@/ui/theme/colors"
import { mediaQueries } from "@/ui/theme/mediaQueries"
import { getNonUmbracoUrl } from "@/utils"
import styled from "@emotion/styled"
import { Box, Link, TextField, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import { Controller, useForm } from "react-hook-form"
import ButtonWithArrow from "../../common/bit/ButtonWithArrow"
import CustomDatePicker from "./CustomDatePicker"

const StyledForm = styled.form`
  width: 100%;
`

const StyledGrid = styled(Box)`
  display: grid;

  ${mediaQueries.desktopM} {
    grid-template-columns: 1fr 1fr;
  }
`

const StyledLabel = styled(Typography)`
  font-weight: 500;
  padding-top: 8px;
  text-align: start;
  color: ${colors.black};
  font-weight: 700;
  padding-bottom: 16px;
  ${mediaQueries.desktopM} {
    padding-bottom: 0px;
    margin: auto 0;
  }
`

const StyledTermsContainer = styled(Box)`
  margin: 24px 0;
`

const StyledButtonContainer = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`

interface CarDetailsFormProps {
  data: any
  onSubmit: (data: any) => void
  isSubmitting: boolean
  disabled?: boolean
}

interface FormData {
  carNumber: string
  day: string
  month: string
  year: string
  termsAgreed: boolean
}

const CarDetailsForm = ({
  data,
  onSubmit,
  isSubmitting,
  disabled = false,
}: CarDetailsFormProps) => {
  const t = useTranslations("ThirdPartyCoverage")

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      carNumber: "",
      day: "",
      month: "",
      year: "",
      termsAgreed: false,
    },
    mode: "onChange",
  })

  const submitForm = (formData: FormData) => {
    // Format as ISO date with time for API (adding T10:30:00 as default time)
    const formattedDate = `${formData.year}-${formData.month.padStart(2, "0")}-${formData.day.padStart(2, "0")}T10:30:00`
    onSubmit({
      licensePlateNumber: formData.carNumber,
      eventDate: formattedDate,
      notFindingCarText:
        data?.notFindingCarText?.value ?? t("defaultNotFindingCarText"),
    })
  }

  // Handler for form errors
  const onError = (errors: any) => {
    console.error("Form validation failed:", errors)
  }

  return (
    <StyledForm onSubmit={handleSubmit(submitForm, onError)} noValidate>
      <StyledGrid>
        <StyledLabel>
          {data?.carNumberLabel?.value || t("carNumberLabel")}
        </StyledLabel>

        <Controller
          name="carNumber"
          control={control}
          rules={{
            required: t("validation.required"),
            minLength: {
              value: 6,
              message: t("validation.carNumberMinLength"),
            },
            maxLength: {
              value: 8,
              message: t("validation.carNumberMaxLength"),
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              type="number"
              error={!!errors.carNumber}
              helperText={errors.carNumber?.message}
              disabled={disabled || isSubmitting}
              InputProps={{
                readOnly: disabled,
                inputProps: {
                  style: { textAlign: "center" },
                  maxLength: 8,
                },
              }}
            />
          )}
        />

        <StyledLabel>
          {data?.accidentDateLabel?.value || t("accidentDateLabel")}
        </StyledLabel>

        <CustomDatePicker
          control={control}
          dayName="day"
          monthName="month"
          yearName="year"
          dayPlaceholder={data?.dayPlaceholder?.value || t("dayPlaceholder")}
          monthPlaceholder={
            data?.monthPlaceholder?.value || t("monthPlaceholder")
          }
          yearPlaceholder={data?.yearPlaceholder?.value || t("yearPlaceholder")}
          dayLabel={t("dayLabel")}
          monthLabel={t("monthLabel")}
          yearLabel={t("yearLabel")}
          disabled={disabled || isSubmitting}
          errors={errors}
        />
      </StyledGrid>

      <StyledTermsContainer>
        {data.termsOfUseLink?.links?.[0] && (
          <Link
            href={getNonUmbracoUrl(data.termsOfUseLink.links[0].url)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {data.termsOfUseLink.links[0].name}
          </Link>
        )}
      </StyledTermsContainer>

      <StyledButtonContainer>
        <ButtonWithArrow
          type="submit"
          colorPallette="red"
          disabled={disabled || isSubmitting}
        >
          {data.buttonText?.value}
        </ButtonWithArrow>
      </StyledButtonContainer>
    </StyledForm>
  )
}

export default CarDetailsForm
